/**
 * GeoNames API service
 * 
 * GeoNames provides a comprehensive database of cities with:
 * - Latitude/longitude coordinates
 * - Timezone information
 * - Population data
 * - Country and region information
 * 
 * Rate Limits (Free Tier):
 * - Daily: 30,000 requests
 * - Hourly: 2,000 requests
 * - Each request = 1 credit
 * 
 * Documentation: https://www.geonames.org/export/web-services.html
 */

import { CityData } from '../data/cities';
import logger from '../utils/logger';

// GeoNames API configuration
const GEONAMES_API_BASE = 'https://secure.geonames.org';
const GEONAMES_USERNAME = process.env.REACT_APP_GEONAMES_USERNAME || 'demo'; // Default demo account

// Debug: Log the username being used (remove in production)
if (process.env.NODE_ENV === 'development') {
  logger.info('GeoNames', 'GeoNames username check', { 
    username: GEONAMES_USERNAME === 'demo' ? 'demo (check .env file)' : GEONAMES_USERNAME,
    isDemo: GEONAMES_USERNAME === 'demo'
  });
}

// Cache for API responses (to reduce API calls)
const searchCache = new Map<string, CityData[]>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface GeoNamesCity {
  geonameId: number;
  name: string;
  countryName: string;
  adminName1?: string; // Region/State
  lat: string;
  lng: string;
  population?: number;
  timezone?: {
    timeZoneId: string;
    gmtOffset: number;
  };
}

interface GeoNamesSearchResponse {
  geonames: GeoNamesCity[];
  totalResultsCount?: number;
}

/**
 * Convert GeoNames city data to our CityData format
 */
function convertToCityData(geonameCity: GeoNamesCity): CityData {
  return {
    name: geonameCity.name,
    country: geonameCity.countryName,
    region: geonameCity.adminName1 || undefined,
    lat: parseFloat(geonameCity.lat),
    lng: parseFloat(geonameCity.lng),
  };
}

/**
 * Search for cities using GeoNames API
 * 
 * @param query - City name, country, or region to search for
 * @param maxResults - Maximum number of results to return (default: 20)
 * @returns Array of CityData matching the search query
 */
export async function searchCities(
  query: string,
  maxResults: number = 20
): Promise<CityData[]> {
  if (!query || query.trim().length < 2) {
    logger.debug('GeoNames', 'Search skipped - query too short', { query, length: query?.length || 0 });
    return [];
  }

  const trimmedQuery = query.trim();
  const cacheKey = `${trimmedQuery.toLowerCase()}_${maxResults}`;

  // Check cache first
  const cached = searchCache.get(cacheKey);
  if (cached) {
    logger.debug('GeoNames', 'Returning cached results', { query: trimmedQuery, resultCount: cached.length });
    return cached;
  }

  try {
    logger.info('GeoNames', 'Searching cities', { query: trimmedQuery, maxResults });
    // GeoNames searchCitiesJSON API
    // Documentation: https://www.geonames.org/export/geonames-search.html
    const url = new URL(`${GEONAMES_API_BASE}/searchJSON`);
    url.searchParams.set('q', trimmedQuery);
    url.searchParams.set('maxRows', maxResults.toString());
    url.searchParams.set('featureClass', 'P'); // P = city, village, etc.
    url.searchParams.set('orderby', 'population'); // Order by population (largest first)
    url.searchParams.set('username', GEONAMES_USERNAME);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`GeoNames API error: ${response.status} ${response.statusText}`);
    }

    const data: GeoNamesSearchResponse = await response.json();

    if (!data.geonames || data.geonames.length === 0) {
      logger.debug('GeoNames', 'No cities found', { query: trimmedQuery });
      // Cache empty results too
      searchCache.set(cacheKey, []);
      return [];
    }

    // Convert to our format and filter out duplicates
    const cities = data.geonames
      .map(convertToCityData)
      .filter((city, index, self) =>
        // Remove duplicates based on name + country
        index === self.findIndex(
          (c) => c.name === city.name && c.country === city.country
        )
      );

    logger.info('GeoNames', 'City search completed', { query: trimmedQuery, resultCount: cities.length, totalResults: data.geonames.length });

    // Cache the results
    searchCache.set(cacheKey, cities);

    // Set cache expiration (simple implementation - could use a proper cache library)
    setTimeout(() => {
      searchCache.delete(cacheKey);
      logger.debug('GeoNames', 'Cache entry expired', { cacheKey });
    }, CACHE_DURATION);

    return cities;
  } catch (error) {
    logger.error('GeoNames', 'City search failed', error, { query: trimmedQuery });
    throw error;
  }
}

/**
 * Get city details by coordinates (reverse geocoding)
 * 
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns CityData for the location
 */
export async function getCityByCoordinates(
  lat: number,
  lng: number
): Promise<CityData | null> {
  logger.info('GeoNames', 'Getting city by coordinates', { lat, lng });
  
  try {
    // GeoNames findNearbyPlaceName API
    const url = new URL(`${GEONAMES_API_BASE}/findNearbyPlaceNameJSON`);
    url.searchParams.set('lat', lat.toString());
    url.searchParams.set('lng', lng.toString());
    url.searchParams.set('username', GEONAMES_USERNAME);

    logger.debug('GeoNames', 'Fetching city by coordinates', { url: url.toString() });
    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`GeoNames API error: ${response.status}`);
    }

    const data: GeoNamesSearchResponse = await response.json();

    if (!data.geonames || data.geonames.length === 0) {
      logger.debug('GeoNames', 'No city found for coordinates', { lat, lng });
      return null;
    }

    const city = convertToCityData(data.geonames[0]);
    logger.info('GeoNames', 'City found by coordinates', { lat, lng, city: city.name, country: city.country });
    return city;
  } catch (error) {
    logger.error('GeoNames', 'Reverse geocoding error', error, { lat, lng });
    return null;
  }
}

/**
 * Get timezone for a city by coordinates
 * 
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Timezone ID (e.g., 'America/New_York')
 */
export async function getTimezoneByCoordinates(
  lat: number,
  lng: number
): Promise<string | null> {
  logger.info('GeoNames', 'Getting timezone by coordinates', { lat, lng });
  
  try {
    // GeoNames timezone API
    const url = new URL(`${GEONAMES_API_BASE}/timezoneJSON`);
    url.searchParams.set('lat', lat.toString());
    url.searchParams.set('lng', lng.toString());
    url.searchParams.set('username', GEONAMES_USERNAME);

    logger.debug('GeoNames', 'Fetching timezone by coordinates', { url: url.toString() });
    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`GeoNames API error: ${response.status}`);
    }

    const data = await response.json();
    const timezone = data.timezoneId || null;
    
    if (timezone) {
      logger.info('GeoNames', 'Timezone found', { lat, lng, timezone });
    } else {
      logger.warn('GeoNames', 'No timezone found for coordinates', { lat, lng });
    }
    
    return timezone;
  } catch (error) {
    logger.error('GeoNames', 'Timezone lookup error', error, { lat, lng });
    return null;
  }
}

/**
 * Clear the search cache
 */
export function clearCache(): void {
  const cacheSize = searchCache.size;
  logger.info('GeoNames', 'Clearing search cache', { cacheSize });
  searchCache.clear();
  logger.debug('GeoNames', 'Search cache cleared', { previousSize: cacheSize });
}

export async function suggestTimezone(lat: number, lng: number): Promise<string | null> {
  logger.info('GeoNames', 'Suggesting timezone', { lat, lng });
  
  try {
    const url = `https://api.geonames.org/timezoneJSON?lat=${lat}&lng=${lng}&username=demo`;
    logger.debug('GeoNames', 'Fetching timezone suggestion', { url });
    const response = await fetch(url);
    const data = await response.json();
    const timezone = data.timezoneId;
    
    if (timezone) {
      logger.info('GeoNames', 'Timezone suggested', { lat, lng, timezone });
    } else {
      logger.warn('GeoNames', 'No timezone suggestion found', { lat, lng });
    }
    
    return timezone;
  } catch (error) {
    logger.error('GeoNames', 'Failed to get timezone suggestion', error, { lat, lng });
    return null;
  }
}

